import React from 'react';
import ZoobbeSelect from '../Global/ZoobbeSelect';

const ActionSelector = ({ actions, actionOptions, onActionsChange, lists, members }) => {
  if (!actionOptions) return null;

  const addAction = () => {
    onActionsChange([...actions, { type: '', config: {} }]);
  };

  const updateAction = (index, key, value) => {
    const updated = [...actions];
    if (key === 'type') {
      updated[index] = { type: value, config: {} };
    } else {
      updated[index] = {
        ...updated[index],
        config: { ...(updated[index].config || {}), [key]: value }
      };
    }
    onActionsChange(updated);
  };

  const removeAction = (index) => {
    onActionsChange(actions.filter((_, i) => i !== index));
  };

  const renderConfigField = (field, action, actionIndex) => {
    const value = (action.config || {})[field.key] ?? '';

    switch (field.type) {
      case 'member_select':
        return (
          <ZoobbeSelect
            key={field.key}
            options={[
              { label: `${field.label}...`, value: '' },
              ...(members?.map(member => ({
                label: member.name || member.user?.name || member.username || member.user?.username,
                value: member._id || member.user?._id
              })) || [])
            ]}
            value={value}
            onSelect={(opt) => updateAction(actionIndex, field.key, opt.value)}
          />
        );

      case 'member_multi_select':
        return (
          <div className="config-field" key={field.key}>
            <label>{field.label}{field.required && ' *'}</label>
            <div className="member-checklist">
              {members?.map(member => {
                const memberId = member._id || member.user?._id;
                const memberName = member.name || member.user?.name || member.username || member.user?.username;
                const configVal = (action.config || {})[field.key];
                const selected = Array.isArray(configVal) && configVal.includes(memberId);
                return (
                  <label key={memberId} className="member-check-item">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={(e) => {
                        const current = Array.isArray(configVal) ? configVal : [];
                        const updated = e.target.checked
                          ? [...current, memberId]
                          : current.filter(id => id !== memberId);
                        updateAction(actionIndex, field.key, updated);
                      }}
                    />
                    <span>{memberName}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );

      case 'list_select':
        return (
          <ZoobbeSelect
            key={field.key}
            options={[
              { label: `${field.label}...`, value: '' },
              ...(lists?.map(list => ({ label: list.title, value: list._id })) || [])
            ]}
            value={value}
            onSelect={(opt) => updateAction(actionIndex, field.key, opt.value)}
          />
        );

      case 'priority_select':
        return (
          <ZoobbeSelect
            key={field.key}
            options={[
              { label: `${field.label}...`, value: '' },
              { label: 'Urgent', value: 'Urgent' },
              { label: 'High', value: 'High' },
              { label: 'Normal', value: 'Normal' },
              { label: 'Low', value: 'Low' }
            ]}
            value={value}
            onSelect={(opt) => updateAction(actionIndex, field.key, opt.value)}
          />
        );

      case 'color_select':
        return (
          <ZoobbeSelect
            key={field.key}
            options={[
              { label: 'Green', value: '#61bd4f' },
              { label: 'Yellow', value: '#f2d600' },
              { label: 'Orange', value: '#ff9f1a' },
              { label: 'Red', value: '#eb5a46' },
              { label: 'Purple', value: '#c377e0' },
              { label: 'Blue', value: '#0079bf' },
              { label: 'Sky', value: '#00c2e0' },
              { label: 'Lime', value: '#51e898' },
              { label: 'Pink', value: '#ff78cb' },
              { label: 'Dark', value: '#344563' }
            ]}
            value={value || '#61bd4f'}
            onSelect={(opt) => updateAction(actionIndex, field.key, opt.value)}
          />
        );

      case 'number':
        return (
          <input
            key={field.key}
            type="number"
            value={value}
            placeholder={field.label}
            onChange={(e) => updateAction(actionIndex, field.key, parseInt(e.target.value) || '')}
          />
        );

      case 'textarea':
        return (
          <textarea
            key={field.key}
            value={value}
            placeholder={field.label}
            onChange={(e) => updateAction(actionIndex, field.key, e.target.value)}
          />
        );

      case 'text':
      default:
        return (
          <input
            key={field.key}
            type="text"
            value={value}
            placeholder={field.label}
            onChange={(e) => updateAction(actionIndex, field.key, e.target.value)}
          />
        );
    }
  };

  const actionTypeOptions = [
    { label: 'Select action...', value: '' },
    ...actionOptions.map(opt => ({ label: opt.label, value: opt.type }))
  ];

  return (
    <div className="action-selector">
      {actions.map((action, index) => {
        const actionDef = actionOptions.find(a => a.type === action.type);
        const hasMultiLineConfig = actionDef?.configFields?.some(
          f => f.type === 'member_multi_select' || f.type === 'textarea'
        );

        return (
          <div key={index} className="action-row">
            <div className="action-row-line">
              <span className="action-number">{index + 1}</span>
              <ZoobbeSelect
                options={actionTypeOptions}
                value={action.type}
                onSelect={(opt) => updateAction(index, 'type', opt.value)}
              />
              {actionDef?.configFields?.filter(f => f.type !== 'member_multi_select' && f.type !== 'textarea').map(
                field => renderConfigField(field, action, index)
              )}
              {actions.length > 1 && (
                <button className="remove-action-btn" onClick={() => removeAction(index)} title="Remove">
                  <span className="material-symbols-outlined">close</span>
                </button>
              )}
              {index === actions.length - 1 && (
                <button className="add-action-inline-btn" onClick={addAction} title="Add action">
                  <span className="material-symbols-outlined">add</span>
                </button>
              )}
            </div>
            {hasMultiLineConfig && actionDef?.configFields?.filter(f => f.type === 'member_multi_select' || f.type === 'textarea').map(
              field => renderConfigField(field, action, index)
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ActionSelector;
